type ClarityFn = ((...args: any[]) => void) & { q?: any[] };

declare global {
  interface Window {
    clarity?: ClarityFn;
    __scAnalyticsState?: {
      maxSectionViewed: number;
      completionFired: boolean;
    };
  }
}

const VIDEO_MILESTONES = [25, 50, 75, 100] as const;

function getClarity(): ClarityFn | null {
  if (typeof window === "undefined") return null;
  return typeof window.clarity === "function" ? window.clarity : null;
}

function setTag(key: string, value: string | number | boolean) {
  const clarity = getClarity();
  if (!clarity) return;
  try {
    clarity("set", key, String(value));
  } catch {}
}

function emitEvent(name: string) {
  const clarity = getClarity();
  if (!clarity) return;
  try {
    clarity("event", name);
  } catch {}
}

function getStorySlug(): string {
  if (typeof document === "undefined") return "storycore";

  const explicit =
    document.querySelector('meta[name="storycore-slug"]')?.getAttribute("content")?.trim() ||
    document.documentElement.getAttribute("data-story-slug")?.trim();
  if (explicit) return explicit;

  const canonical = document.querySelector('link[rel="canonical"]')?.getAttribute("href")?.trim();
  const source = canonical || (typeof window !== "undefined" ? window.location.pathname : "");

  try {
    const url = new URL(source, typeof window !== "undefined" ? window.location.href : "http://localhost");
    const filename = (url.pathname.split("/").pop() || "").replace(/\.[^.]+$/, "");
    return filename || "storycore";
  } catch {
    return (source.split("/").pop() || "storycore").replace(/\.[^.]+$/, "") || "storycore";
  }
}

function getTotalSections(): number {
  if (typeof document === "undefined") return 0;
  return document.querySelectorAll("section[data-section-index]").length;
}

function getSectionIndex(section: HTMLElement): number {
  const raw = section.dataset.sectionIndex || section.getAttribute("data-section-index") || "0";
  const value = Number(raw);
  return Number.isFinite(value) ? value : 0;
}

function getSectionKey(section: HTMLElement): string {
  const idx = getSectionIndex(section);
  return section.id || `section-${idx}`;
}

function setStoryContext(section?: HTMLElement, extras?: Record<string, string | number | boolean>) {
  setTag("story_slug", getStorySlug());
  const total = getTotalSections();
  if (total > 0) setTag("story_total_sections", total);

  if (section) {
    setTag("story_section_index", getSectionIndex(section));
    setTag("story_section_key", getSectionKey(section));
  }

  if (extras) {
    Object.entries(extras).forEach(([key, value]) => setTag(key, value));
  }
}

function getAnalyticsState() {
  if (typeof window === "undefined") {
    return { maxSectionViewed: -1, completionFired: false };
  }
  if (!window.__scAnalyticsState) {
    window.__scAnalyticsState = { maxSectionViewed: -1, completionFired: false };
  }
  return window.__scAnalyticsState;
}

function progressBucket(percent: number): 25 | 50 | 75 | 100 {
  if (percent >= 100) return 100;
  if (percent >= 75) return 75;
  if (percent >= 50) return 50;
  return 25;
}

function getVideoLabel(video: HTMLVideoElement): string {
  const src =
    video.currentSrc ||
    video.getAttribute("src") ||
    video.querySelector("source")?.getAttribute("src") ||
    "";
  if (!src) return "unknown-video";

  try {
    const url = new URL(src, typeof window !== "undefined" ? window.location.href : "http://localhost");
    return (url.pathname.split("/").pop() || "unknown-video").replace(/\.[^.]+$/, "") || "unknown-video";
  } catch {
    return (src.split("/").pop() || "unknown-video").replace(/\.[^.]+$/, "") || "unknown-video";
  }
}

export function trackStorySectionView(section: HTMLElement) {
  const idx = getSectionIndex(section);
  const total = getTotalSections();
  const state = getAnalyticsState();

  setStoryContext(section, {
    story_sections_seen_max: Math.max(state.maxSectionViewed + 1, idx + 1),
    story_progress_percent: total > 0 ? Math.round(((idx + 1) / total) * 100) : 0,
  });

  if (section.dataset.scClaritySectionSeen !== "1") {
    section.dataset.scClaritySectionSeen = "1";
    emitEvent("story_section_view");
  }

  if (idx > state.maxSectionViewed) {
    state.maxSectionViewed = idx;

    const sectionsSeen = idx + 1;
    const percent = total > 0 ? Math.round((sectionsSeen / total) * 100) : 0;
    const bucket = progressBucket(percent);

    setStoryContext(section, {
      story_sections_seen_max: sectionsSeen,
      story_progress_percent: percent,
      story_progress_bucket: bucket,
    });

    emitEvent("story_progress");
    emitEvent(`story_progress_${bucket}`);

    if (total > 0 && sectionsSeen >= total && !state.completionFired) {
      state.completionFired = true;
      setStoryContext(section, {
        story_sections_seen_max: sectionsSeen,
        story_progress_percent: 100,
        story_progress_bucket: 100,
      });
      emitEvent("story_complete");
    }
  }
}

export function instrumentReelVideo(video: HTMLVideoElement, section: HTMLElement) {
  if ((video as any).__scClarityBound) return;
  (video as any).__scClarityBound = true;

  const fired = new Set<number>();
  let lastPauseAt = -1;

  const pushVideoContext = () => {
    const duration = Number.isFinite(video.duration) ? Math.round(video.duration) : 0;
    setStoryContext(section, {
      reel_video_label: getVideoLabel(video),
      reel_duration_seconds: duration,
    });
  };

  const onPlay = () => {
    pushVideoContext();
    emitEvent("reel_play");
  };

  const onPause = () => {
    const current = Math.floor(video.currentTime || 0);
    if (current === lastPauseAt && video.ended) return;
    lastPauseAt = current;
    pushVideoContext();
    setTag("reel_pause_seconds", current);
    emitEvent("reel_pause");
  };

  const onEnded = () => {
    pushVideoContext();
    setTag("reel_watch_percent", 100);
    emitEvent("reel_complete");
  };

  const onTimeUpdate = () => {
    const duration = video.duration;
    if (!duration || !Number.isFinite(duration) || duration <= 0) return;

    const percent = Math.floor((video.currentTime / duration) * 100);
    setTag("reel_watch_percent", Math.max(0, Math.min(percent, 100)));

    VIDEO_MILESTONES.forEach((milestone) => {
      if (percent >= milestone && !fired.has(milestone)) {
        fired.add(milestone);
        pushVideoContext();
        setTag("reel_watch_percent", milestone);
        emitEvent(`reel_${milestone}`);
      }
    });
  };

  video.addEventListener("play", onPlay, { passive: true });
  video.addEventListener("pause", onPause, { passive: true });
  video.addEventListener("ended", onEnded, { passive: true });
  video.addEventListener("timeupdate", onTimeUpdate, { passive: true });
}
